/**
 * Iter-181-B — Campaign attribution capture + persistence.
 *
 * Stores the user's first-seen UTM params (utm_source / utm_medium /
 * utm_campaign / utm_content / utm_term) + referrer + landing path
 * into sessionStorage on first page-view, then exposes a helper that
 * lead-capture forms can call to attach the attribution dict to the
 * outbound quote-request payload.
 *
 * Why first-seen vs. last-seen?
 *   • Most paid-ad networks attribute revenue on first-click for
 *     publisher reporting. NerdWallet/Bankrate use first-seen.
 *   • Also reduces "internal-page refresh wipes attribution" bugs.
 *
 * Why sessionStorage vs. localStorage?
 *   • Privacy-first. Attribution expires when the session ends; we
 *     never store campaign data beyond the visit.
 *   • Avoids cross-visit data leaks between users on shared devices.
 *
 * Backend persistence:
 *   • `POST /api/quote-requests` accepts an optional `attribution`
 *     object on the payload. The server persists it onto the
 *     quote_requests row for funnel-attribution reporting.
 */

const STORAGE_KEY = "carsavr.attribution.v1";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

const EXTRA_KEYS = [
  "gclid",   // Google Ads click ID
  "fbclid",  // Meta Ads click ID
  "msclkid", // Bing Ads click ID
  "ttclid",  // TikTok Ads click ID
];

const ALL_KEYS = [...UTM_KEYS, ...EXTRA_KEYS];

function readStored() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage?.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStored(data) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* private mode — no-op */
  }
}

/**
 * Capture attribution from the current page's querystring + document
 * referrer. Should be called once per app load (App.jsx mount). Subsequent
 * calls are no-ops UNLESS new UTMs are present on the URL (handles
 * mid-session paid-ad re-entry).
 */
export function captureAttribution() {
  if (typeof window === "undefined") return null;
  let stored = readStored();
  let url;
  try {
    url = new URL(window.location.href);
  } catch {
    return stored;
  }

  // Pull whatever attribution params are on the current URL.
  const incoming = {};
  for (const k of ALL_KEYS) {
    const v = url.searchParams.get(k);
    if (v) incoming[k] = String(v).slice(0, 256);
  }
  const hasIncoming = Object.keys(incoming).length > 0;

  if (stored && !hasIncoming) {
    return stored; // First-seen wins; nothing new to record.
  }

  // First seen OR new UTM params on a mid-session URL → overwrite. We
  // overwrite the entire dict (not merge) because mixing two campaigns
  // would produce a Frankenstein attribution that no analytics tool
  // would interpret cleanly.
  const record = {
    ...incoming,
    first_landing_path: stored?.first_landing_path || url.pathname || "/",
    first_referrer:
      stored?.first_referrer ||
      (document.referrer ? String(document.referrer).slice(0, 256) : null),
    captured_at: new Date().toISOString(),
  };
  writeStored(record);
  return record;
}

/** Returns the stored attribution dict (or null if none yet captured). */
export function getAttribution() {
  return readStored();
}

/**
 * Helper: merge the stored attribution into a payload before sending it
 * to `POST /api/quote-requests`. Drops empty fields so the backend row
 * stays compact.
 *
 *   const payload = withAttribution({
 *     zip_code: "85140",
 *     category: "insurance",
 *     email: "user@example.com",
 *   });
 *   await api.post("/quote-requests", payload);
 */
export function withAttribution(payload) {
  const attr = getAttribution();
  if (!attr) return payload;
  // Strip empty values; backend has its own size guard but we save
  // bandwidth by trimming here.
  const trimmed = {};
  for (const [k, v] of Object.entries(attr)) {
    if (v != null && v !== "") trimmed[k] = v;
  }
  if (Object.keys(trimmed).length === 0) return payload;
  return { ...payload, attribution: trimmed };
}

/** Clear stored attribution (used by tests + sign-out hooks). */
export function clearAttribution() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage?.removeItem(STORAGE_KEY);
  } catch {
    /* no-op */
  }
}
